import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { setupListeners } from "@reduxjs/toolkit/query";
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // Default storage (localStorage for web)

// Importing services
import { AuthService } from "../Services/AuthService";
import { CategoryService } from "../Services/CategoryService";
import { ProductService } from '../Services/ProductService';
import { CustomerService } from '../Services/CustomerService';
import { CouponService } from '../Services/CouponService';
import { OrderService } from '../Services/OrderService';
import { faqApi } from '../Services/FAQService';
import { CountryService } from '../Services/CountryService';
import { SettingsService } from '../Services/SettingsService';
import { CustomerPortalLayoutService } from '../Services/CustomerPortalLayoutService';
import { HelpService } from '../Services/HelpService';
import { RatingAndReviewService } from '../Services/RatingAndReviewService';
import { BusBookingService } from '../Services/BusBookingService';
import { RechargeService } from '../Services/RechargeService';
import { RefundService } from '../Services/RefundService';

// Importing slices
import AuthSlice from './Slices/AuthSlice';
import NotificationSlice from './Slices/NotificationSlice';
import ThemeSlice from './Slices/ThemeSlice';
import SettingSlice from './Slices/SettingSlice';
import CustomerPortalLayoutSlice from './Slices/CustomerPortalLayoutSlice';
import AddToCartSlice from './Slices/AddToCartSlice';
import BusBookingSlice from "./Slices/BusBookingSlice";
import RechargeSlice from "./Slices/RechargeSlice";
import ToastSlice from './Slices/ToastSlice';

// import { lazy } from 'react';

// Persist configurations
const authPersistConfig = {
    key: 'auth',
    storage,
    blacklist: ['accessTokenExpiry'], // Don't persist expiry - calculate from token
};

const themePersistConfig = {
    key: 'theme',
    storage,
};

const settingPersistConfig = {
    key: 'setting',
    storage,
};


const CartsPersistConfig = {
    key: 'Carts',
    storage,
};

const BusBookingConfig = {
    key: 'BusBooking',
    storage,
};


// Combine reducers
const rootReducer = combineReducers({
    AuthSlice: persistReducer(authPersistConfig, AuthSlice.reducer), // Persisted
    NotificationSlice: NotificationSlice.reducer, // Not persisted
    ToastSlice: ToastSlice.reducer, // Not persisted
    RechargeSlice: RechargeSlice.reducer, // Not persisted
    ThemeSlice: persistReducer(themePersistConfig, ThemeSlice.reducer), // Persisted
    SettingSlice: persistReducer(settingPersistConfig, SettingSlice.reducer), // Persisted
    /**
     * The home-page layout is NOT persisted, deliberately.
     *
     * It was, to localStorage, with no version and no expiry -- so every
     * browser rendered its own saved copy of the shop first and only corrected
     * it once the layout request came back. Four people testing the same site
     * saw four different home pages, each a snapshot from whenever that browser
     * last loaded one, and anyone whose request failed kept theirs
     * indefinitely. Unpublishing a section could not be relied on to reach
     * anybody.
     *
     * This is server-owned content and it is fetched on every home-page load
     * anyway (Recommended.tsx), so persisting it only bought a slightly faster
     * first paint in exchange for showing people a shop that no longer exists.
     */
    CustomerPortalLayoutSlice: CustomerPortalLayoutSlice.reducer, // Not persisted: see above
    AddToCartSlice: persistReducer(CartsPersistConfig, AddToCartSlice.reducer), // Persisted
    BusBookingSlice: persistReducer(BusBookingConfig,BusBookingSlice.reducer),
    [AuthService.reducerPath]: AuthService.reducer,
    [CategoryService.reducerPath]: CategoryService.reducer,
    [ProductService.reducerPath]: ProductService.reducer,
    [CustomerService.reducerPath]: CustomerService.reducer,
    [CouponService.reducerPath]: CouponService.reducer,
    [OrderService.reducerPath]: OrderService.reducer,
    [faqApi.reducerPath]: faqApi.reducer,
    [SettingsService.reducerPath]: SettingsService.reducer,
    [CountryService.reducerPath]: CountryService.reducer,
    [HelpService.reducerPath]: HelpService.reducer,
    [CustomerPortalLayoutService.reducerPath]: CustomerPortalLayoutService.reducer,
    [RatingAndReviewService.reducerPath]: RatingAndReviewService.reducer,
    [BusBookingService.reducerPath]: BusBookingService.reducer,
    [RechargeService.reducerPath]: RechargeService.reducer,
    [RefundService.reducerPath]: RefundService.reducer,
});

// Configure the store
export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
            },
        }).concat(
            AuthService.middleware,
            CategoryService.middleware,
            ProductService.middleware,
            CustomerService.middleware,
            CouponService.middleware,
            OrderService.middleware,
            faqApi.middleware,
            SettingsService.middleware,
            CountryService.middleware,
            HelpService.middleware,
            CustomerPortalLayoutService.middleware,
            RatingAndReviewService.middleware,
            BusBookingService.middleware,
            RechargeService.middleware,
            RefundService.middleware
        ),
});

// Persistor
export const persistor = persistStore(store);

// Listeners
setupListeners(store.dispatch);

// Types
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

export default store;
